import type { SkillKey, Skills, Survivor } from './types'

export const CORE_SKILLS: SkillKey[] = ['looting', 'stealth', 'endurance']
export const SURVIVOR_PROGRESS_SKILL: SkillKey = 'survival'

export const BOOK_SKILL_MAP: Record<string, SkillKey> = {
  'book-home-cooking': 'cooking',
  'book-archery-starter': 'archery',
  'book-swimming-fundamentals': 'swimming',
  'book-driving-theory': 'driving',
  'book-field-first-aid': 'first_aid',
  'book-hacking-utrecht': 'programming',
  'book-bartering-highway': 'bartering',
}

const clampLevel = (n: number) => Math.max(1, Math.min(10, Math.round(n)))

export const xpForSkillLevel = (level: number): number =>
  Math.max(20, Math.round(25 * Math.pow(1.35, level - 1)))

export function createStarterSkills(seed?: Partial<Record<SkillKey, number>>): Skills {
  const out: Skills = {}
  for (const key of CORE_SKILLS) {
    out[key] = {
      level: clampLevel(seed?.[key] ?? 1),
      xp: 0,
      unlocked: true,
    }
  }
  out[SURVIVOR_PROGRESS_SKILL] = {
    level: clampLevel(seed?.[SURVIVOR_PROGRESS_SKILL] ?? 1),
    xp: 0,
    unlocked: true,
  }
  return out
}

export function ensureSurvivorSkills(raw: Skills | undefined): Skills {
  if (!raw) return createStarterSkills()
  const out: Skills = { ...raw }
  for (const key of CORE_SKILLS) {
    const state = out[key]
    if (typeof state === 'number') {
      out[key] = { level: clampLevel(state), xp: 0, unlocked: true }
      continue
    }
    if (!state) {
      out[key] = { level: 1, xp: 0, unlocked: true }
      continue
    }
    out[key] = {
      level: clampLevel(state.level ?? 1),
      xp: Math.max(0, state.xp ?? 0),
      unlocked: true,
    }
  }
  const progression = out[SURVIVOR_PROGRESS_SKILL]
  if (!progression) {
    out[SURVIVOR_PROGRESS_SKILL] = { level: 1, xp: 0, unlocked: true }
  } else {
    out[SURVIVOR_PROGRESS_SKILL] = {
      level: clampLevel(progression.level ?? 1),
      xp: Math.max(0, progression.xp ?? 0),
      unlocked: true,
    }
  }
  return out
}

export function getSkillLevel(survivor: Survivor, key: SkillKey): number {
  const state = survivor.skills[key]
  return state?.unlocked ? clampLevel(state.level) : 0
}

export function isSkillUnlocked(survivor: Survivor, key: SkillKey): boolean {
  return survivor.skills[key]?.unlocked ?? false
}

export function unlockSkill(skills: Skills, key: SkillKey): Skills {
  const current = skills[key]
  return {
    ...skills,
    [key]: {
      level: clampLevel(current?.level ?? 1),
      xp: Math.max(0, current?.xp ?? 0),
      unlocked: true,
    },
  }
}

export function grantSkillXp(skills: Skills, key: SkillKey, amount: number): Skills {
  if (amount <= 0) return skills
  const current = skills[key]
  if (!current?.unlocked) return skills
  let level = clampLevel(current.level)
  let xp = current.xp + amount
  while (level < 10) {
    const needed = xpForSkillLevel(level)
    if (xp < needed) break
    xp -= needed
    level += 1
  }
  if (level >= 10) xp = 0
  return {
    ...skills,
    [key]: { ...current, level, xp },
  }
}
